import { useEffect, useRef, useState } from 'react';
import { Brain, ArrowRight, Search, FileText } from 'lucide-react';
import { THICK_TILE_CLASSNAME, THICK_TILE_STYLE } from './tileStyles';

interface TutorReadyItem {
  contentId: string;
  title: string;
}

interface TutorEntryTileProps {
  eligibleContent: TutorReadyItem[];
  tutorSubtitle: string;
  onNavigate: (path: string) => void;
}

export function TutorEntryTile({ eligibleContent, tutorSubtitle, onNavigate }: TutorEntryTileProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const hasContent = eligibleContent.length > 0;

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = query.trim()
    ? eligibleContent.filter((c) => c.title.toLowerCase().includes(query.trim().toLowerCase()))
    : eligibleContent;

  function handlePick(contentId: string) {
    setOpen(false);
    onNavigate(`/learn/${contentId}?tutor=open`);
  }

  return (
    <div ref={containerRef} className="relative flex-1 flex">
      <div
        className={`${THICK_TILE_CLASSNAME} flex-1 p-4 relative overflow-hidden flex flex-col`}
        style={THICK_TILE_STYLE}
      >
        <div
          className="absolute pointer-events-none"
          style={{
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            background: 'radial-gradient(circle, rgba(10,191,188,0.18) 0%, transparent 65%)',
          }}
        />
        <div className="relative flex items-start gap-3 flex-1">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-teal-500/12 flex items-center justify-center">
            <Brain size={16} className="text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-[15px] font-semibold">Professor Cogni</div>
            <div className="text-xs text-content-muted mt-0.5">AI tutor · {tutorSubtitle}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={hasContent ? () => setOpen((v) => !v) : () => onNavigate('/upload')}
          className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body font-semibold transition-colors cursor-pointer
            ${hasContent
              ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-400 hover:to-teal-500 shadow-[0_2px_10px_-3px_rgba(10,191,188,0.45)]'
              : 'bg-dark-subtle border border-dark-border text-content-muted hover:text-content hover:border-indigo-400/40'}`}
        >
          {hasContent ? (
            <>
              Ask Professor Cogni
              <ArrowRight size={14} />
            </>
          ) : (
            <>Upload content first</>
          )}
        </button>
      </div>

      {open && hasContent && (
        <div className="absolute z-30 left-0 right-0 top-full mt-2 rounded-xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden">
          {eligibleContent.length > 5 && (
            <div className="p-2 border-b border-dark-border">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-dim" />
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search your content"
                  className="w-full pl-7 pr-2 py-1.5 rounded-md bg-dark-subtle border border-dark-border text-xs text-content font-body focus:outline-none focus:border-teal-400/60"
                />
              </div>
            </div>
          )}
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-content-dim font-semibold">
            Pick content to chat about
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-xs text-content-muted">No matches</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.contentId}
                  type="button"
                  onClick={() => handlePick(c.contentId)}
                  className="w-full text-left px-3 py-2.5 hover:bg-dark-hover transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <FileText size={13} className="text-teal-400 shrink-0" />
                  <span className="text-sm font-body text-content truncate">{c.title}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
